const ACCESS_KEY = 'mm_access_token';
const REFRESH_KEY = 'mm_refresh_token';

export const getAccessToken = () => localStorage.getItem(ACCESS_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export function setTokens({ accessToken, refreshToken }) {
  if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// Single-flight refresh. The app fires several authenticated requests at once
// (the admin dashboard alone makes ~6), so when the access token expires they
// all 401 together. The refresh endpoint ROTATES the refresh token — it revokes
// the old one as it issues a new pair — so if each 401 kicked off its own
// refresh, the first would succeed and every other would race on a
// now-revoked token, fail, and clearTokens(), logging the user out
// ("invalid token"). De-duplicating to one in-flight refresh fixes that.
let refreshPromise = null;

function refreshAccess() {
  if (refreshPromise) return refreshPromise;
  const rt = getRefreshToken();
  if (!rt) return Promise.resolve(null);
  refreshPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) {
        clearTokens();
        return null;
      }
      const data = await res.json();
      setTokens(data);
      return data.accessToken;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function api(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  const accessToken = getAccessToken();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  let res = await fetch(path, { ...options, headers });

  if (res.status === 401 && getRefreshToken() && !options._retried) {
    const newToken = await refreshAccess();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      res = await fetch(path, { ...options, headers, _retried: true });
    }
  }

  return res;
}

export async function apiJson(path, options = {}) {
  const res = await api(path, {
    ...options,
    body: options.body && typeof options.body !== 'string'
      ? JSON.stringify(options.body)
      : options.body,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    /* no body */
  }
  if (!res.ok) {
    const message = data?.error || `Request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}
